import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import { VIGNETTE } from '../../lib/medievalTheme';
import { isDevBuild } from '../../utils/devMode';
import { levelFromXp } from '../../utils/arena/profileDefaults';
import { getClass } from '../../utils/arena/classes';
import { getSkillState, normalizeSkillState, getAvailableSkillPoints, TOTAL_SKILL_POINTS } from '../../utils/arena/skills';
import { t } from '../../localization/t';
import NightSky from './NightSky';
import ClassPickerScreen from './ClassPickerScreen';
import ClassTreeScreen from './ClassTreeScreen';

const SKY = 'radial-gradient(ellipse 80% 60% at 50% 110%, rgba(38, 44, 66, 0.55) 0%, transparent 60%), linear-gradient(180deg, #090b12 0%, #05060a 100%)';
const FADE = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };

// Mastery. One sky, two views: the class picker until a class is
// chosen, the class's constellation afterwards. A paid reset on the
// tree clears the class and drops the player back onto the picker.
//
// `viewProfile` shows another player's tree read-only.
export default class MasteryScreen extends Component {
  constructor(props) {
    super(props);
    this.state = {
      skillState: props.viewProfile ? normalizeSkillState(props.viewProfile.skills) : getSkillState(),
      accent: null,
      // Browsing: the picker shown over an already chosen class, so the
      // player can read every calling before paying to change. A class
      // with a tree opens as a read-only preview.
      browsing: false,
      previewKey: null,
      // Class under the eye on the picker; drives the sky sign.
      focusKey: null,
    };
  }

  handleSkillState = (next) => {
    this.setState({ skillState: next, browsing: false, previewKey: null });
    this.props.onSkillState?.(next);
  };

  handleBrowse = () => this.setState({ browsing: true, previewKey: null, accent: null });

  handleStopBrowsing = () => this.setState({ browsing: false, previewKey: null, accent: null });

  handlePreview = (key) => {
    if (key === this.state.skillState.classKey) { this.handleStopBrowsing(); return; }
    this.setState({ previewKey: key });
  };

  handleBackToBrowse = () => this.setState({ previewKey: null });

  handleAccent = (accent) => {
    if (accent !== this.state.accent) this.setState({ accent });
  };

  handleFocus = (focusKey) => {
    if (focusKey !== this.state.focusKey) this.setState({ focusKey });
  };

  // Whose tree is on screen: another player's when viewing, else our own.
  get subject() { return this.props.viewProfile || this.props.profile; }

  // Dev/admin always gets the level cap and the full budget so the
  // gates never block testing while a tree is being iterated on.
  get devUnlocked() { return isDevBuild() && Boolean(this.props.isAdmin); }

  getEffectiveLevel() {
    if (this.devUnlocked) return 30;
    return levelFromXp(this.subject?.xp || 0);
  }

  getAvailablePoints() {
    if (this.devUnlocked) return TOTAL_SKILL_POINTS;
    return getAvailableSkillPoints(this.subject);
  }

  render() {
    const { onClose, viewProfile, profile } = this.props;
    const { skillState, browsing, previewKey } = this.state;
    const readOnly = Boolean(viewProfile);
    const viewName = readOnly ? (viewProfile.username || viewProfile.name || null) : null;
    const cls = getClass(skillState.classKey);
    const preview = previewKey ? getClass(previewKey) : null;
    const accent = preview?.accent || (browsing ? this.state.accent || cls?.accent : cls?.accent || this.state.accent);
    // The sign in the sky: the previewed class, else the class under the
    // eye while picking or browsing, else the held class behind its tree.
    // On the overview nothing is drawn until a sign is hovered or picked.
    const pickerOpen = browsing || !cls;
    const signKey = preview?.key || (pickerOpen ? this.state.focusKey : cls?.key) || null;

    return (
      <div className="fixed inset-0 z-[100] overflow-hidden" style={{ background: SKY }} data-testid="mastery-screen">
        <NightSky accent={accent} figureKey={signKey} humoursSign={Boolean(preview?.key === 'alchemist' || (!pickerOpen && cls?.key === 'alchemist'))} subdued={!pickerOpen || Boolean(preview)} />
        <AnimatePresence>
          {accent ? (
            <motion.div
              key={accent}
              className="absolute inset-0 pointer-events-none"
              {...FADE}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              style={{ background: `radial-gradient(ellipse 70% 55% at 50% 35%, ${accent}26 0%, ${accent}0a 40%, transparent 70%)` }}
            />
          ) : null}
        </AnimatePresence>
        <div className="absolute inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        <AnimatePresence mode="wait">
          {readOnly && !cls ? (
            <motion.div key="none" className="absolute inset-0" {...FADE}>
              <ClassTreeEmpty name={viewName} onClose={onClose} />
            </motion.div>
          ) : preview ? (
            <motion.div key={`preview-${preview.key}`} className="absolute inset-0" {...FADE} transition={{ duration: 0.35 }}>
              <ClassTreeScreen
                skillState={{ classKey: preview.key, pointsSpent: [] }}
                profile={profile}
                readOnly
                effectiveLevel={this.getEffectiveLevel()}
                onBack={this.handleBackToBrowse}
                onClose={onClose}
              />
            </motion.div>
          ) : cls && !browsing ? (
            <motion.div key={`tree-${cls.key}`} className="absolute inset-0" {...FADE} transition={{ duration: 0.35 }}>
              <ClassTreeScreen
                skillState={skillState}
                profile={profile}
                oracle={this.subject?.oracle}
                battlemage={this.subject?.battlemage}
                readOnly={readOnly}
                viewName={viewName}
                availablePoints={this.getAvailablePoints()}
                effectiveLevel={this.getEffectiveLevel()}
                onSkillState={this.handleSkillState}
                onCoins={this.props.onCoins}
                onBack={readOnly ? undefined : this.handleBrowse}
                onClose={onClose}
              />
            </motion.div>
          ) : (
            <motion.div key="picker" className="absolute inset-0" {...FADE} transition={{ duration: 0.35 }}>
              <ClassPickerScreen
                currentKey={cls?.key || null}
                onChosen={this.handleSkillState}
                onPreview={this.handlePreview}
                onBack={cls ? this.handleStopBrowsing : undefined}
                onAccentChange={this.handleAccent}
                onFocusChange={this.handleFocus}
                onClose={onClose}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }
}

function ClassTreeEmpty({ name, onClose }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center" onClick={onClose}>
      <p style={{ color: 'rgba(232, 213, 160, 0.7)', fontSize: '1cqw' }}>
        {name ? t('{name} has not chosen a class yet.', { name }) : t('No class chosen yet.')}
      </p>
    </div>
  );
}
